import ChannelContext from '@jetshop/core/components/ChannelContext';
import get from 'lodash.get';
import React, { useContext } from 'react';
import styled from 'react-emotion';
import Helmet from 'react-helmet-async';
import theme from '@jetshop/ui/utils/theme';
//import ProductConfigurationContext from '@jetshop/core/components/ProductConfigurationProvider/ProductConfigurationContext';
import ProductVariationContext from '@jetshop/core/components/ProductVariationProvider/ProductVariationContext';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
//import StockStatusContainer from '@jetshop/ui/Stock/StockStatusContainer';
import MaxWidth from '../Layout/MaxWidth';
import ProductInfo from './ProductInfo';
import QuantityContainer from './QuantityContainer';
import Badges from '@jetshop/ui/ProductList/Badges';
import { Price } from '../Price';
import BackButton from './BackButton';
import Layout from '../Layout/Layout';
import BuyArea from './BuyArea';
import RelatedProducts from './RelatedProducts';
import Selectors from './Selectors';
import Images from './Images';
import ProductPagePlaceholder from './ProductPagePlaceholder';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { Form } from 'formik';
import AddToCartForm from './AddToCart/AddToCartForm';
//import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';

const ProductWrapper = styled('div')``;

const ProductSection = styled('section')`
  ${({ theme }) => theme.below.md} {
    margin: 0;
    margin-bottom: 20px;
    width: 100%;
  }
  /* ${({ theme }) => theme.above.md} {
    width: ${({ theme }) =>
      `calc(50% - ${theme.space[2]} - ${theme.space[2]})`};
  } */
`;

const ProductImagesSection = styled(ProductSection)`
  ${p => p.theme.above.sm} {
    width: 50%;
    padding-right: 30px;
  }

  ${p => p.theme.above.md} {
    padding-right: 30px;
    width: 60%;
  }

  ${p => p.theme.below.md} {
    margin-bottom: 0;
  }
`;

const ProductInfoSection = styled(ProductSection)`
  ${p => p.theme.above.sm} {
    width: 50%;
  }

  ${p => p.theme.above.md} {
    width: 40%;
  }
`;

const Container = styled('div')`
  display: flex;
  width: 100%;
  /* flex-wrap: wrap; */
  background-color: white;

  ${({ theme }) => theme.below.lg} {
    padding-bottom: 0;
  }

  ${({ theme }) => theme.below.sm} {
    flex-direction: column;
  }
`;

const Heading = styled('h1')`
  font-weight: bold;
  font-size: 22px;
  color: #444;
`;

const SubHeading = styled('h2')`
  margin-bottom: 10px;
  color: #888;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.25px;
`;

const ProductContainer = styled('div')`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
`;

const ProductDetailsWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${Price.Wrapper} {
  }
  ${Price.Normal} {
    font-size: 1.5rem;
    font-weight: ${theme('typo.fontWeightBold')};
    color: ${theme('colors.orange')};
  }
  ${Price.Old} {
  }
  ${Price.New} {
    font-size: 1.5rem;
    font-weight: ${theme('typo.fontWeightBold')};
    color: ${theme('colors.product.discountPrice')};
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
  ${({ theme }) => theme.below.md} {
    align-items: center;
  }
`;

function getBrand(product) {
  const field = product.customFields.find(({ key }) => key === 'brand');
  if (field) {
    return field.stringValue;
  }
}

const ProductMeta = ({ product, selectedVariation }) => {
  const { selectedChannel } = useContext(ChannelContext);

  const prod = selectedVariation || product;
  const brand = getBrand(product);

  return (
    <Helmet>
      <meta property="og:title" content={product.name} />
      <meta
        property="product:original_price:amount"
        content={prod.previousPrice.incVat}
      />
      <meta property="product:price:amount" content={prod.price.incVat} />
      <meta
        property="og:price:currency"
        content={selectedChannel.currency.id}
      />
      {product.images.length > 0 && (
        <meta property="og:image" content={product.images[0].url} />
      )}
      <meta
        property="product:availability"
        content={prod.stockStatus.buyable ? 'instock' : 'oos'}
      />
      {brand && <meta property="product:brand" content={brand} />}
      <meta
        property="product:category"
        content={product.canonicalCategory && product.canonicalCategory.name}
      />
      <meta
        property="og:url"
        content={`${selectedChannel.url}${product.primaryRoute.path}`}
      />
    </Helmet>
  );
};

const Product = ({ result: { data, loading }, product, cacheHit }) => {
  const { selectedVariation } = useContext(ProductVariationContext);
  //const configurationContext = useContext(ProductConfigurationContext);
  const stockStatus = useStockStatus(selectedVariation || product);
  const price = useDynamicPrice(product);
  const images = get(product, 'images', []);

  // Return early if there is no cache hit
  if (!cacheHit || loading) {
    return <ProductPagePlaceholder />;
  }
  const parents = getParentOrCanonicalRoute(
    get(data, 'route.parents'),
    product
  );

  const breadcrumbProps = {
    parents,
    breadcrumbText: product.breadcrumbText
  };

  const relatedProducts = get(product, 'relatedProducts');

  return (
    <Layout breadcrumbs={breadcrumbProps} route={data.route}>
      <ProductWrapper>
        <ProductContainer>
          <AddToCartForm>
            {({ setValues, values }) => (
              <>
                <MaxWidth />
                <Container>
                  <ProductMeta {...{ product, selectedVariation }} />

                  <ProductImagesSection>
                    <Images images={images} aspect={null}>
                      <Badges badges={product.badges} />
                    </Images>
                  </ProductImagesSection>

                  <ProductInfoSection>
                    <QuantityContainer>
                      {({ quantity, setQuantity }) => (
                        <ProductDetailsWrapper>
                          <Heading data-testid="product-title">
                            {product.mainHeader.length > 0
                              ? product.mainHeader
                              : product.name || 'Loading...'}
                          </Heading>

                          <SubHeading>{product.subName}</SubHeading>
                          <StyledForm>
                            <Selectors
                              quantity={quantity}
                              setQuantity={setQuantity}
                              setValues={setValues}
                              values={values}
                              variants={product.variants || null}
                            />

                            <BuyArea
                              stockStatus={stockStatus}
                              quantity={quantity}
                              {...price}
                            />
                          </StyledForm>

                          <ProductInfo product={product} />
                        </ProductDetailsWrapper>
                      )}
                    </QuantityContainer>
                  </ProductInfoSection>
                </Container>
                {relatedProducts && (
                  <RelatedProducts relatedProducts={relatedProducts} />
                )}
                <BackButton />
              </>
            )}
          </AddToCartForm>
        </ProductContainer>
      </ProductWrapper>
    </Layout>
  );
};

export default Product;
